import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { api, formatBytes, formatDate } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { 
  FileText, 
  MagnifyingGlass, 
  CloudArrowUp,
  DownloadSimple,
  Trash,
  Eye,
  FilePdf,
  FileDoc,
  FileXls,
  FilePpt,
  FileImage,
  File as FileIcon,
  Cube,
  Tag,
  X,
  PencilSimple,
  Funnel,
  Check,
  Archive,
  CheckCircle,
  DotsThreeVertical,
  Receipt,
  Truck,
  CurrencyCircleDollar,
  ShoppingCart,
  ArrowCounterClockwise,
  Clock,
  XCircle,
  CaretUp,
  CaretDown,
  CaretUpDown,
  FlowArrow,
  CaretRight,
  Folder,
  FolderOpen,
  House,
  ClockCounterClockwise,
  Warning,
  NotePencil
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const getFileIcon = (contentType, filename) => {
  const filenameLC = (filename || '').toLowerCase();
  
  // PDF
  if (contentType?.includes('pdf')) return FilePdf;
  
  // Office documents
  if (contentType?.includes('word') || contentType?.includes('document') || 
      filenameLC.endsWith('.doc') || filenameLC.endsWith('.docx') || filenameLC.endsWith('.odt')) return FileDoc;
  if (contentType?.includes('excel') || contentType?.includes('spreadsheet') ||
      filenameLC.endsWith('.xls') || filenameLC.endsWith('.xlsx') || filenameLC.endsWith('.ods')) return FileXls;
  if (contentType?.includes('presentation') || contentType?.includes('powerpoint') ||
      filenameLC.endsWith('.ppt') || filenameLC.endsWith('.pptx') || filenameLC.endsWith('.odp')) return FilePpt;
  
  // Images
  if (contentType?.includes('image') || 
      filenameLC.endsWith('.jpg') || filenameLC.endsWith('.jpeg') || filenameLC.endsWith('.png') ||
      filenameLC.endsWith('.gif') || filenameLC.endsWith('.webp') || filenameLC.endsWith('.tiff') || 
      filenameLC.endsWith('.tif')) return FileImage;
  
  // CAD files
  if (filenameLC.endsWith('.dwg') || filenameLC.endsWith('.dxf')) return Cube;
  
  // Text files
  if (contentType?.includes('text') || filenameLC.endsWith('.txt') || filenameLC.endsWith('.csv') ||
      filenameLC.endsWith('.md') || filenameLC.endsWith('.log')) return FileText;
      
  return FileIcon;
};

const isPreviewable = (contentType, filename) => {
  if (!contentType) return false;
  const filenameLC = (filename || '').toLowerCase();
  
  // PDF - native preview
  if (contentType.includes('pdf')) return true;
  
  // Images - native preview (including WebP, TIFF)
  if (contentType.includes('image')) return true;
  if (filenameLC.endsWith('.webp') || filenameLC.endsWith('.tiff') || filenameLC.endsWith('.tif')) return true;
  
  // Text files - native preview
  if (contentType.includes('text')) return true;
  if (filenameLC.endsWith('.txt') || filenameLC.endsWith('.csv') || filenameLC.endsWith('.md') || filenameLC.endsWith('.log')) return true;
  
  // Office documents - preview via PDF/A conversion
  if (filenameLC.endsWith('.doc') || filenameLC.endsWith('.docx') || filenameLC.endsWith('.odt')) return true;
  if (filenameLC.endsWith('.xls') || filenameLC.endsWith('.xlsx') || filenameLC.endsWith('.ods')) return true;
  if (filenameLC.endsWith('.ppt') || filenameLC.endsWith('.pptx') || filenameLC.endsWith('.odp')) return true;
  
  // CAD files - preview via PDF/A conversion
  if (filenameLC.endsWith('.dxf')) return true;
  // DWG requires special handling (not fully supported yet)
  if (filenameLC.endsWith('.dwg')) return false;
  
  // Email files - preview via PDF/A conversion
  if (filenameLC.endsWith('.eml') || filenameLC.endsWith('.msg')) return true;
  
  return false;
};

// Document type styling helper
const getDocumentTypeStyle = (type) => {
  const styles = {
    fattura: { icon: Receipt, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Fattura' },
    contratto: { icon: FileText, color: 'text-purple-600', bg: 'bg-purple-50', label: 'Contratto' },
    ddt: { icon: Truck, color: 'text-orange-600', bg: 'bg-orange-50', label: 'DDT' },
    preventivo: { icon: CurrencyCircleDollar, color: 'text-green-600', bg: 'bg-green-50', label: 'Preventivo' },
    ordine: { icon: ShoppingCart, color: 'text-cyan-600', bg: 'bg-cyan-50', label: 'Ordine' },
    nota_credito: { icon: ArrowCounterClockwise, color: 'text-red-600', bg: 'bg-red-50', label: 'Nota Credito' },
    altro: { icon: FileIcon, color: 'text-gray-500', bg: 'bg-gray-50', label: 'Altro' }
  };
  return styles[type] || styles.altro;
};

// Build folder tree from flat array
const buildFolderTree = (folders) => {
  const folderMap = {};
  const roots = [];
  
  // Create a map of all folders
  folders.forEach(folder => {
    folderMap[folder.id] = { ...folder, children: [] };
  });
  
  // Build the tree structure
  folders.forEach(folder => {
    if (folder.parent_id && folderMap[folder.parent_id]) {
      folderMap[folder.parent_id].children.push(folderMap[folder.id]);
    } else {
      roots.push(folderMap[folder.id]);
    }
  });
  
  // Sort each level alphabetically
  const sortChildren = (nodes) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    nodes.forEach(node => {
      if (node.children.length > 0) {
        sortChildren(node.children);
      }
    });
  };
  
  sortChildren(roots);
  return roots;
};

// Folder Tree Dialog Component
const FolderTreeDialog = ({ open, onClose, folders, selectedFolder, onSelect, rootLabel, rootValue = 'none', t }) => {
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [tempSelected, setTempSelected] = useState(selectedFolder);
  const treeData = useMemo(() => buildFolderTree(folders), [folders]);
  
  // Reset temp selection when dialog opens
  useEffect(() => {
    if (open) {
      setTempSelected(selectedFolder);
    }
  }, [open, selectedFolder]);
  
  const toggleExpand = (folderId, e) => {
    e.stopPropagation();
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };
  
  const handleConfirm = () => {
    onSelect(tempSelected);
    onClose();
  };
  
  const renderTreeNode = (node, level = 0) => {
    const isExpanded = expandedFolders.has(node.id);
    const isSelected = tempSelected === node.id;
    const hasChildren = node.children && node.children.length > 0;
    
    return (
      <div key={node.id}>
        <div
          className={`flex items-center gap-2 py-2 px-3 cursor-pointer rounded-sm transition-colors ${
            isSelected ? 'bg-[#002FA7] text-white' : 'hover:bg-[#F4F4F5]'
          }`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => setTempSelected(node.id)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => toggleExpand(node.id, e)}
              className={`p-0.5 rounded-sm ${isSelected ? 'hover:bg-[#001d6a]' : 'hover:bg-[#E4E4E7]'}`}
            >
              {isExpanded ? (
                <CaretDown size={14} className={isSelected ? 'text-white' : 'text-[#52525B]'} />
              ) : (
                <CaretRight size={14} className={isSelected ? 'text-white' : 'text-[#52525B]'} />
              )}
            </button>
          ) : (
            <span className="w-5" />
          )}
          {isExpanded ? (
            <FolderOpen size={18} className={isSelected ? 'text-white' : 'text-[#F5B041]'} />
          ) : (
            <Folder size={18} className={isSelected ? 'text-white' : 'text-[#F5B041]'} />
          )}
          <span className="text-sm font-medium truncate">{node.name}</span>
        </div>
        
        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Folder size={20} className="text-[#F5B041]" />
            {t('documents.selectFolder')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="border border-[#E4E4E7] rounded-sm max-h-80 overflow-auto bg-white">
          {/* Root folder option */}
          <div
            className={`flex items-center gap-2 py-2 px-3 cursor-pointer rounded-sm transition-colors ${
              tempSelected === rootValue ? 'bg-[#002FA7] text-white' : 'hover:bg-[#F4F4F5]'
            }`}
            onClick={() => setTempSelected(rootValue)}
          >
            <span className="w-5" />
            <House size={18} className={tempSelected === rootValue ? 'text-white' : 'text-[#52525B]'} />
            <span className="text-sm font-medium">{rootLabel}</span>
          </div>
          
          {/* Folder tree */}
          {treeData.map(node => renderTreeNode(node, 0))}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm">
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleConfirm}
            className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
          >
            <Check size={16} className="mr-2" />
            {t('common.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DocumentsPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPdfaDoc, setPreviewPdfaDoc] = useState(null);
  const [previewPdfaUrl, setPreviewPdfaUrl] = useState(null);
  
  // Edit tags state
  const [editTagsDoc, setEditTagsDoc] = useState(null);
  const [editTagsValue, setEditTagsValue] = useState('');
  const [savingTags, setSavingTags] = useState(false);
  
  // Tag filter state
  const [filterTags, setFilterTags] = useState([]);
  const [matchAllTags, setMatchAllTags] = useState(false);
  const [showTagFilter, setShowTagFilter] = useState(false);
  
  // Upload form state
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadTags, setUploadTags] = useState('');
  const [uploadFolder, setUploadFolder] = useState('none');
  const [showFolderTreeDialog, setShowFolderTreeDialog] = useState(false);
  const [showFilterFolderDialog, setShowFilterFolderDialog] = useState(false);
  
  // Workflow approval state
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalDoc, setApprovalDoc] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [selectedApprover, setSelectedApprover] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [submittingApproval, setSubmittingApproval] = useState(false);

  // Workflow history state
  const [showWorkflowHistory, setShowWorkflowHistory] = useState(false);
  const [workflowHistory, setWorkflowHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Version history state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistoryDoc, setVersionHistoryDoc] = useState(null);
  const [documentVersions, setDocumentVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  
  // Duplicate detection state
  const [duplicateFile, setDuplicateFile] = useState(null);
  const [duplicateExisting, setDuplicateExisting] = useState(null);
  const [duplicateIsSameContent, setDuplicateIsSameContent] = useState(false);
  const [pendingUploads, setPendingUploads] = useState([]);

  // Sorting state
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const localeMap = { it: 'it-CH', de: 'de-CH', fr: 'fr-CH', en: 'en-GB' };

  // Fetch all tags for autocomplete
  const fetchAllTags = async () => {
    try {
      const res = await api.getAllTags();
      setAllTags(res.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      
      // If we have tag filters, use the special endpoint
      if (filterTags.length > 0) {
        const folderId = selectedFolder === 'all' ? null : selectedFolder;
        const res = await api.getDocumentsByTags(filterTags.join(','), matchAllTags, folderId);
        setDocuments(res.data);
      } else {
        const folderId = selectedFolder === 'all' ? null : selectedFolder;
        const res = await api.getDocuments(folderId, search || null);
        setDocuments(res.data);
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [selectedFolder, search, filterTags, matchAllTags, t]);

  // Sort documents
  const sortedDocuments = [...documents].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    // Handle special cases
    if (sortField === 'original_filename') {
      aVal = aVal?.toLowerCase() || '';
      bVal = bVal?.toLowerCase() || '';
    } else if (sortField === 'size' || sortField === 'classification_confidence') {
      aVal = aVal || 0;
      bVal = bVal || 0;
    } else if (sortField === 'document_type_label') {
      aVal = aVal || 'altro';
      bVal = bVal || 'altro';
    } else if (sortField === 'workflow_status') {
      aVal = aVal || '';
      bVal = bVal || '';
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const fetchFolders = async () => {
    try {
      const res = await api.getAllFolders();
      setFolders(res.data);
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
  };

  useEffect(() => {
    fetchFolders();
    fetchAllTags();
  }, []);

  // Helper to get selected folder name
  const getSelectedFolderName = () => {
    if (uploadFolder === 'none') return t('folders.root');
    const folder = folders.find(f => f.id === uploadFolder);
    return folder ? folder.name : t('folders.root');
  };

  // Helper to get filter folder name
  const getFilterFolderName = () => {
    if (selectedFolder === 'all') return t('folders.root');
    const folder = folders.find(f => f.id === selectedFolder);
    return folder ? folder.name : t('folders.root');
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchDocuments();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchDocuments]);

  // Dropzone for upload dialog
  const onDrop = useCallback((acceptedFiles) => {
    setUploadFiles(acceptedFiles);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true
  });

  // Helper function to calculate file hash
  const calculateFileHash = async (file) => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    
    setUploading(true);
    const folderId = uploadFolder === 'none' ? null : uploadFolder;
    const tags = uploadTags.trim() || null;
    
    try {
      for (const file of uploadFiles) {
        // Calculate hash for duplicate detection
        const fileHash = await calculateFileHash(file);
        
        // Check for duplicates
        const checkRes = await api.checkDocumentDuplicate(file.name, folderId, fileHash);
        const { exists, existing_document, is_same_content } = checkRes.data;
        
        if (exists && existing_document) {
          if (is_same_content) {
            // Same content - just skip with notification
            toast.info(`${file.name}: ${t('documents.duplicateSameContent')}`);
            continue;
          }
          
          // Different content, same name - ask for confirmation
          setDuplicateFile(file);
          setDuplicateExisting(existing_document);
          setDuplicateIsSameContent(is_same_content);
          setPendingUploads(uploadFiles.filter(f => f !== file));
          setUploading(false);
          return; // Wait for user decision
        }
        
        // No duplicate - upload normally
        await api.uploadDocument(file, folderId, tags);
      }
      toast.success(t('documents.uploadSuccess'));
      setShowUploadDialog(false);
      setUploadFiles([]);
      setUploadTags('');
      setUploadFolder('none');
      fetchDocuments();
      fetchAllTags();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('common.error');
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  // Handle duplicate resolution
  const handleDuplicateDecision = async (decision) => {
    const folderId = uploadFolder === 'none' ? null : uploadFolder;
    const tags = uploadTags.trim() || null;
    
    setUploading(true);
    
    try {
      if (decision === 'update') {
        // Update existing with new version
        await api.uploadDocument(duplicateFile, folderId, tags, duplicateExisting.id);
        toast.success(`${t('documents.versionUpdated')} ${duplicateExisting.version + 1}`);
      } else if (decision === 'skip') {
        // Skip this file
        toast.info(`${duplicateFile.name}: ${t('documents.skipFile')}`);
      }
      // 'keepBoth' would need to rename the file - not implemented for now
      
      // Reset duplicate state
      setDuplicateFile(null);
      setDuplicateExisting(null);
      setDuplicateIsSameContent(false);
      
      // Continue with remaining files
      if (pendingUploads.length > 0) {
        for (const file of pendingUploads) {
          const fileHash = await calculateFileHash(file);
          const checkRes = await api.checkDocumentDuplicate(file.name, folderId, fileHash);
          const { exists, existing_document, is_same_content } = checkRes.data;
          
          if (exists && existing_document) {
            if (is_same_content) {
              toast.info(`${file.name}: ${t('documents.duplicateSameContent')}`);
              continue;
            }
            setDuplicateFile(file);
            setDuplicateExisting(existing_document);
            setDuplicateIsSameContent(is_same_content);
            setPendingUploads(pendingUploads.filter(f => f !== file));
            setUploading(false);
            return;
          }
          await api.uploadDocument(file, folderId, tags);
        }
      }
      
      setPendingUploads([]);
      setShowUploadDialog(false);
      setUploadFiles([]);
      setUploadTags('');
      setUploadFolder('none');
      fetchDocuments();
      fetchAllTags();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('common.error');
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  // Fetch document versions
  const handleViewVersions = async (doc) => {
    setVersionHistoryDoc(doc);
    setShowVersionHistory(true);
    setLoadingVersions(true);
    
    try {
      const res = await api.getDocumentVersions(doc.id);
      setDocumentVersions(res.data);
    } catch (error) {
      toast.error(t('common.error'));
      setDocumentVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  };

  // Download specific version
  const handleDownloadVersion = async (versionId) => {
    try {
      const doc = documentVersions.find(v => v.id === versionId);
      if (doc) {
        await api.downloadDocument(versionId, `v${doc.version}_${versionHistoryDoc.original_filename}`);
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const openUploadDialog = () => {
    setUploadFiles([]);
    setUploadTags('');
    setUploadFolder(selectedFolder === 'all' ? 'none' : selectedFolder);
    setShowUploadDialog(true);
  };

  const handleDownload = async (doc) => {
    try {
      await api.downloadDocument(doc.id, doc.original_filename);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleDownloadPdfa = async (doc) => {
    try {
      await api.downloadPdfA(doc.id, doc.original_filename);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async () => {
    if (!deleteDoc) return;
    try {
      await api.deleteDocument(deleteDoc.id);
      toast.success(t('common.success'));
      setDeleteDoc(null);
      fetchDocuments();
      fetchAllTags();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Workflow approval handlers
  const openApprovalDialog = async (doc) => {
    setApprovalDoc(doc);
    setSelectedApprover('');
    setApprovalNotes('');
    setShowApprovalDialog(true);
    
    // Fetch approvers
    try {
      const res = await api.getApprovers();
      setApprovers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch approvers:', error);
      toast.error('Errore nel caricamento degli approvatori');
    }
  };

  const handleSubmitForApproval = async () => {
    if (!approvalDoc || !selectedApprover) {
      toast.error('Seleziona un approvatore');
      return;
    }
    
    setSubmittingApproval(true);
    try {
      await api.submitForApproval(approvalDoc.id, selectedApprover, approvalNotes);
      toast.success('Documento inviato per approvazione');
      setShowApprovalDialog(false);
      setApprovalDoc(null);
      fetchDocuments();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore nell\'invio';
      toast.error(msg);
    } finally {
      setSubmittingApproval(false);
    }
  };

  // Load workflow history for a document
  const loadWorkflowHistory = async (doc) => {
    if (!doc.workflow_id) {
      toast.error('Nessun workflow associato a questo documento');
      return;
    }
    
    setLoadingHistory(true);
    try {
      const res = await api.getWorkflow(doc.workflow_id);
      setWorkflowHistory(res.data);
      setShowWorkflowHistory(true);
    } catch (error) {
      toast.error('Errore nel caricamento dello storico');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handlePreview = async (doc) => {
    if (!isPreviewable(doc.content_type, doc.filename)) {
      toast.error(t('documents.noPreview'));
      return;
    }
    
    setPreviewDoc(doc);
    setPreviewLoading(true);
    
    try {
      const blob = await api.getDocumentPreviewBlob(doc.id);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      toast.error(t('common.error'));
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewDoc(null);
    setPreviewUrl(null);
  };

  const handlePreviewPdfa = (doc) => {
    if (!doc.has_pdfa) {
      toast.error(t('documents.pdfaNotAvailable'));
      return;
    }
    const pdfaUrl = api.getDocumentPreviewPdfaUrl(doc.id);
    setPreviewPdfaDoc(doc);
    setPreviewPdfaUrl(pdfaUrl);
  };

  const closePreviewPdfa = () => {
    setPreviewPdfaDoc(null);
    setPreviewPdfaUrl(null);
  };

  const removeUploadFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Edit tags handlers
  const openEditTags = (doc) => {
    setEditTagsDoc(doc);
    setEditTagsValue(doc.tags?.join(', ') || '');
  };

  const handleSaveTags = async () => {
    if (!editTagsDoc) return;
    
    setSavingTags(true);
    try {
      const tags = editTagsValue.split(',').map(t => t.trim()).filter(t => t);
      await api.updateDocumentTags(editTagsDoc.id, tags);
      toast.success(t('documents.tagsUpdated'));
      setEditTagsDoc(null);
      setEditTagsValue('');
      fetchDocuments();
      fetchAllTags();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSavingTags(false);
    }
  };

  // Tag filter handlers
  const toggleTagFilter = (tag) => {
    setFilterTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearTagFilters = () => {
    setFilterTags([]);
    setShowTagFilter(false);
  };

  // Suggested tags for autocomplete (filter out already used in input)
  const suggestedTags = useMemo(() => {
    const currentTags = uploadTags.toLowerCase().split(',').map(t => t.trim()).filter(t => t);
    return allTags.filter(t => !currentTags.includes(t.tag.toLowerCase())).slice(0, 8);
  }, [allTags, uploadTags]);

  const addSuggestedTag = (tag) => {
    const current = uploadTags.trim();
    if (current) {
      setUploadTags(current + ', ' + tag);
    } else {
      setUploadTags(tag);
    }
  };

  // Suggested tags for edit dialog
  const editSuggestedTags = useMemo(() => {
    const currentTags = editTagsValue.toLowerCase().split(',').map(t => t.trim()).filter(t => t);
    return allTags.filter(t => !currentTags.includes(t.tag.toLowerCase())).slice(0, 8);
  }, [allTags, editTagsValue]);

  const addEditSuggestedTag = (tag) => {
    const current = editTagsValue.trim();
    if (current) {
      setEditTagsValue(current + ', ' + tag);
    } else {
      setEditTagsValue(tag);
    }
  };

  return (
    <Layout>
      <div className="page-header">
        <div className="flex items-center justify-between">
          <h1 className="page-title" data-testid="documents-title">
            {t('documents.title')}
          </h1>
          <Button
            onClick={openUploadDialog}
            className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
            data-testid="upload-btn"
          >
            <CloudArrowUp size={20} className="mr-2" />
            {t('documents.upload')}
          </Button>
        </div>
      </div>

      <div className="content-area">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <Input
              placeholder={t('documents.searchHint') || t('documents.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 input-swiss"
              data-testid="search-input"
              disabled={filterTags.length > 0}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowFilterFolderDialog(true)}
            className="w-[200px] justify-start rounded-sm h-10 text-left font-normal"
            data-testid="folder-filter-btn"
          >
            <Folder size={16} className="mr-2 text-[#F5B041]" />
            <span className="truncate flex-1">{getFilterFolderName()}</span>
            <CaretRight size={14} className="ml-auto text-[#A1A1AA]" />
          </Button>
          
          {/* Tag Filter Button */}
          <Popover open={showTagFilter} onOpenChange={setShowTagFilter}>
            <PopoverTrigger asChild>
              <Button 
                variant={filterTags.length > 0 ? "default" : "outline"} 
                className={`rounded-sm ${filterTags.length > 0 ? 'bg-[#002FA7] text-white' : ''}`}
                data-testid="tag-filter-btn"
              >
                <Funnel size={18} className="mr-2" />
                {t('documents.filterByTags')}
                {filterTags.length > 0 && (
                  <Badge className="ml-2 bg-white text-[#002FA7]">{filterTags.length}</Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="start">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm">{t('documents.filterByTags')}</h4>
                  {filterTags.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={clearTagFilters}
                      className="text-xs text-[#E32636] hover:text-[#E32636]"
                    >
                      {t('documents.clearFilter')}
                    </Button>
                  )}
                </div>
                
                {/* Match mode toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#52525B]">
                    {matchAllTags ? t('documents.matchAll') : t('documents.matchAny')}
                  </span>
                  <Switch 
                    checked={matchAllTags} 
                    onCheckedChange={setMatchAllTags}
                    data-testid="match-all-toggle"
                  />
                </div>
                
                {/* Available tags */}
                <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                  {allTags.length === 0 ? (
                    <p className="text-sm text-[#A1A1AA]">{t('common.noData')}</p>
                  ) : (
                    allTags.map((tagObj) => (
                      <button
                        key={tagObj.tag}
                        onClick={() => toggleTagFilter(tagObj.tag)}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm border transition-colors ${
                          filterTags.includes(tagObj.tag)
                            ? 'bg-[#002FA7] text-white border-[#002FA7]'
                            : 'bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#002FA7]'
                        }`}
                        data-testid={`filter-tag-${tagObj.tag}`}
                      >
                        {filterTags.includes(tagObj.tag) && <Check size={12} />}
                        {tagObj.tag}
                        <span className="opacity-60">({tagObj.count})</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Active filters display */}
        {filterTags.length > 0 && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-[#E3F2FD] rounded-sm">
            <span className="text-sm font-medium text-[#002FA7]">{t('documents.activeFilters')}:</span>
            <div className="flex flex-wrap gap-1">
              {filterTags.map(tag => (
                <Badge 
                  key={tag}
                  className="bg-[#002FA7] text-white cursor-pointer hover:bg-[#002075]"
                  onClick={() => toggleTagFilter(tag)}
                >
                  {tag}
                  <X size={12} className="ml-1" />
                </Badge>
              ))}
            </div>
            <span className="text-xs text-[#52525B] ml-2">
              ({matchAllTags ? t('documents.matchAll') : t('documents.matchAny')})
            </span>
          </div>
        )}

        {/* Documents Table */}
        <div className="bg-white border border-[#E4E4E7]">
          {loading ? (
            <div className="p-8 text-center text-[#52525B]">
              {t('common.loading')}
            </div>
          ) : documents.length === 0 ? (
            <div className="empty-state py-16">
              <FileText className="empty-state-icon" size={48} />
              <p className="empty-state-title">{t('documents.noDocuments')}</p>
            </div>
          ) : (
            <table className="w-full table-swiss">
              <thead>
                <tr>
                  <th 
                    className="text-left px-6 py-4 cursor-pointer hover:bg-[#F4F4F5] select-none"
                    onClick={() => handleSort('original_filename')}
                  >
                    <div className="flex items-center gap-1">
                      {t('documents.name')}
                      {sortField === 'original_filename' ? (
                        sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />
                      ) : <CaretUpDown size={14} className="text-[#A1A1AA]" />}
                    </div>
                  </th>
                  <th 
                    className="text-left px-6 py-4 cursor-pointer hover:bg-[#F4F4F5] select-none"
                    onClick={() => handleSort('size')}
                  >
                    <div className="flex items-center gap-1">
                      {t('documents.size')}
                      {sortField === 'size' ? (
                        sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />
                      ) : <CaretUpDown size={14} className="text-[#A1A1AA]" />}
                    </div>
                  </th>
                  <th 
                    className="text-left px-6 py-4 cursor-pointer hover:bg-[#F4F4F5] select-none"
                    onClick={() => handleSort('document_type_label')}
                  >
                    <div className="flex items-center gap-1">
                      {t('documents.documentType')}
                      {sortField === 'document_type_label' ? (
                        sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />
                      ) : <CaretUpDown size={14} className="text-[#A1A1AA]" />}
                    </div>
                  </th>
                  <th 
                    className="text-left px-6 py-4 cursor-pointer hover:bg-[#F4F4F5] select-none"
                    onClick={() => handleSort('has_pdfa')}
                  >
                    <div className="flex items-center gap-1">
                      {t('documents.pdfa')}
                      {sortField === 'has_pdfa' ? (
                        sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />
                      ) : <CaretUpDown size={14} className="text-[#A1A1AA]" />}
                    </div>
                  </th>
                  <th 
                    className="text-left px-6 py-4 cursor-pointer hover:bg-[#F4F4F5] select-none"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      {t('documents.date')}
                      {sortField === 'created_at' ? (
                        sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />
                      ) : <CaretUpDown size={14} className="text-[#A1A1AA]" />}
                    </div>
                  </th>
                  <th 
                    className="text-left px-6 py-4 cursor-pointer hover:bg-[#F4F4F5] select-none"
                    onClick={() => handleSort('workflow_status')}
                  >
                    <div className="flex items-center gap-1">
                      {t('documents.status')}
                      {sortField === 'workflow_status' ? (
                        sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />
                      ) : <CaretUpDown size={14} className="text-[#A1A1AA]" />}
                    </div>
                  </th>
                  <th className="text-right px-6 py-4">{t('documents.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedDocuments.map((doc) => {
                  const Icon = getFileIcon(doc.content_type, doc.filename);
                  const canPreview = isPreviewable(doc.content_type, doc.filename);
                  const typeStyle = getDocumentTypeStyle(doc.document_type_label || 'altro');
                  const TypeIcon = typeStyle.icon;
                  return (
                    <tr key={doc.id} data-testid={`doc-row-${doc.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="document-icon">
                            <Icon size={20} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-[#09090B]">
                                {doc.original_filename}
                              </span>
                              {doc.version > 1 && (
                                <Badge 
                                  variant="outline" 
                                  className="text-[10px] px-1.5 py-0 text-[#002FA7] border-[#002FA7] cursor-pointer hover:bg-blue-50"
                                  onClick={() => handleViewVersions(doc)}
                                  title={t('documents.viewVersions')}
                                >
                                  v{doc.version}
                                </Badge>
                              )}
                            </div>
                            <span className="hash-display text-[10px] mt-0.5 block" title={doc.hash_sha256}>
                              {doc.hash_sha256.substring(0, 16)}...
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#52525B]">
                        {formatBytes(doc.size)}
                      </td>
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm ${typeStyle.bg}`}>
                          <TypeIcon size={14} className={typeStyle.color} weight="fill" />
                          <span className={`text-xs font-medium ${typeStyle.color}`}>
                            {t(`documents.types.${doc.document_type_label || 'altro'}`)}
                          </span>
                          {doc.classification_confidence > 0 && (
                            <span className="text-[10px] text-gray-400">
                              {Math.round(doc.classification_confidence)}%
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {doc.has_pdfa ? (
                          <div className="flex items-center gap-1">
                            <CheckCircle size={16} className="text-green-600" weight="fill" />
                            <span className="text-xs text-green-700 font-medium">PDF/A-1b</span>
                            {doc.pdfa_size && (
                              <span className="text-[10px] text-[#A1A1AA] ml-1">
                                ({formatBytes(doc.pdfa_size)})
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-[#A1A1AA]">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#52525B]">
                        {formatDate(doc.created_at, localeMap[language])}
                      </td>
                      <td className="px-6 py-4">
                        {doc.workflow_status === 'pending' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded">
                            <Clock size={12} />
                            {t('documents.workflowStatus.pending')}
                          </span>
                        )}
                        {doc.workflow_status === 'changes_requested' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded">
                            <PencilSimple size={12} />
                            {t('documents.workflowStatus.changes_requested')}
                          </span>
                        )}
                        {doc.workflow_status === 'approved' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                            <CheckCircle size={12} weight="fill" />
                            {t('documents.workflowStatus.approved')}
                          </span>
                        )}
                        {doc.workflow_status === 'archived' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                            <CheckCircle size={12} weight="fill" />
                            {t('documents.workflowStatus.archived')}
                          </span>
                        )}
                        {doc.workflow_status === 'rejected' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
                            <XCircle size={12} />
                            {t('documents.workflowStatus.rejected')}
                          </span>
                        )}
                        {!doc.workflow_status && (
                          <span className="text-xs text-[#A1A1AA]">{t('documents.workflowStatus.none')}</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          {/* View Details - Always visible */}
                          <button
                            onClick={() => setViewDoc(doc)}
                            className="p-2 hover:bg-[#F4F4F5] transition-colors rounded-sm"
                            title={t('documents.view')}
                            data-testid={`view-doc-${doc.id}`}
                          >
                            <FileText size={18} className="text-[#52525B]" />
                          </button>
                          
                          {/* Preview - Always visible if previewable */}
                          {canPreview && (
                            <button
                              onClick={() => handlePreview(doc)}
                              className="p-2 hover:bg-[#F4F4F5] transition-colors rounded-sm"
                              title={t('documents.preview')}
                              data-testid={`preview-doc-${doc.id}`}
                            >
                              <Eye size={18} className="text-[#52525B]" />
                            </button>
                          )}
                          
                          {/* Three dots menu for other actions */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="p-2 hover:bg-[#F4F4F5] transition-colors rounded-sm"
                                data-testid={`doc-menu-${doc.id}`}
                              >
                                <DotsThreeVertical size={18} className="text-[#52525B]" weight="bold" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem 
                                onClick={() => handleDownload(doc)}
                                className="cursor-pointer"
                              >
                                <DownloadSimple size={16} className="mr-2 text-[#002FA7]" />
                                {t('documents.download')}
                              </DropdownMenuItem>
                              
                              {doc.has_pdfa && (
                                <DropdownMenuItem 
                                  onClick={() => handleDownloadPdfa(doc)}
                                  className="cursor-pointer"
                                >
                                  <Archive size={16} className="mr-2 text-green-600" />
                                  {t('documents.downloadPdfa')}
                                </DropdownMenuItem>
                              )}
                              
                              <DropdownMenuSeparator />
                              
                              {/* Version History */}
                              <DropdownMenuItem 
                                onClick={() => handleViewVersions(doc)}
                                className="cursor-pointer"
                              >
                                <ClockCounterClockwise size={16} className="mr-2 text-[#52525B]" />
                                {t('documents.viewVersions')}
                                {doc.version > 1 && (
                                  <Badge variant="outline" className="ml-auto text-xs">
                                    v{doc.version}
                                  </Badge>
                                )}
                              </DropdownMenuItem>
                              
                              {/* Meta Note - only for PDF or documents with PDF/A */}
                              {(doc.has_pdfa || doc.content_type === 'application/pdf' || 
                                doc.original_filename?.toLowerCase().endsWith('.pdf')) && (
                                <DropdownMenuItem 
                                  onClick={() => navigate(`/metanote/${doc.id}`)}
                                  className="cursor-pointer"
                                >
                                  <NotePencil size={16} className="mr-2 text-[#002FA7]" />
                                  {t('metanote.openMetaNote') || 'Meta Note'}
                                </DropdownMenuItem>
                              )}
                              
                              <DropdownMenuItem 
                                onClick={() => openEditTags(doc)}
                                className="cursor-pointer"
                              >
                                <Tag size={16} className="mr-2 text-[#52525B]" />
                                {t('documents.editTags')}
                              </DropdownMenuItem>
                              
                              {/* Send for approval - only if not already in workflow */}
                              {!doc.workflow_status && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => openApprovalDialog(doc)}
                                    className="cursor-pointer text-[#002FA7]"
                                  >
                                    <CheckCircle size={16} className="mr-2" />
                                    Invia per Approvazione
                                  </DropdownMenuItem>
                                </>
                              )}
                              
                              <DropdownMenuSeparator />
                              
                              <DropdownMenuItem 
                                onClick={() => setDeleteDoc(doc)}
                                className="cursor-pointer text-[#E32636] focus:text-[#E32636]"
                              >
                                <Trash size={16} className="mr-2" />
                                {t('documents.delete')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Upload Dialog with Tags and Autocomplete */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('documents.upload')}</DialogTitle>
          </DialogHeader>
          
          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`dropzone p-8 text-center cursor-pointer ${isDragActive ? 'dropzone-active' : ''}`}
            data-testid="dropzone"
          >
            <input {...getInputProps()} data-testid="file-input" />
            <CloudArrowUp size={40} className="mx-auto mb-3 text-[#A1A1AA]" />
            <p className="text-sm text-[#52525B]">
              {isDragActive ? t('documents.dropzoneActive') : t('documents.dropzone')}
            </p>
          </div>

          {/* Selected Files */}
          {uploadFiles.length > 0 && (
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {uploadFiles.map((file, idx) => (
                <div key={idx} className="flex items-center justify-between bg-[#F4F4F5] px-3 py-2 rounded-sm">
                  <span className="text-sm text-[#09090B] truncate flex-1">{file.name}</span>
                  <span className="text-xs text-[#52525B] mx-2">{formatBytes(file.size)}</span>
                  <button 
                    onClick={() => removeUploadFile(idx)}
                    className="p-1 hover:bg-[#E4E4E7] rounded-sm"
                  >
                    <X size={14} className="text-[#52525B]" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Folder Selection - Button with Dialog */}
          <div>
            <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-2 block">
              {t('documents.selectFolder')}
            </Label>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowFolderTreeDialog(true)}
              className="w-full justify-start rounded-sm h-10 text-left font-normal"
              data-testid="select-folder-btn"
            >
              <Folder size={16} className="mr-2 text-[#F5B041]" />
              <span className="truncate">{getSelectedFolderName()}</span>
              <CaretRight size={14} className="ml-auto text-[#A1A1AA]" />
            </Button>
          </div>

          {/* Tags Input with Autocomplete */}
          <div>
            <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
              <Tag size={14} className="inline mr-1" />
              {t('documents.tags')}
            </Label>
            <Input
              value={uploadTags}
              onChange={(e) => setUploadTags(e.target.value)}
              placeholder={t('documents.tagsPlaceholder')}
              className="input-swiss mt-1"
              data-testid="upload-tags-input"
            />
            <p className="text-xs text-[#A1A1AA] mt-1">{t('documents.tagsHint')}</p>
            
            {/* Suggested Tags */}
            {suggestedTags.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-[#52525B] mb-1">{t('documents.suggestedTags')}:</p>
                <div className="flex flex-wrap gap-1">
                  {suggestedTags.map((tagObj) => (
                    <button
                      key={tagObj.tag}
                      onClick={() => addSuggestedTag(tagObj.tag)}
                      className="inline-flex items-center px-2 py-0.5 text-xs bg-[#F4F4F5] text-[#52525B] rounded-sm hover:bg-[#E4E4E7] transition-colors"
                      type="button"
                    >
                      + {tagObj.tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowUploadDialog(false)}
              className="rounded-sm"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || uploadFiles.length === 0}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
              data-testid="upload-confirm-btn"
            >
              {uploading ? t('documents.uploading') : t('documents.upload')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Folder Tree Selection Dialog */}
      <FolderTreeDialog
        open={showFolderTreeDialog}
        onClose={() => setShowFolderTreeDialog(false)}
        folders={folders}
        selectedFolder={uploadFolder}
        onSelect={setUploadFolder}
        rootLabel={t('folders.root')}
        t={t}
      />

      {/* Folder Tree Selection Dialog for Filter */}
      <FolderTreeDialog
        open={showFilterFolderDialog}
        onClose={() => setShowFilterFolderDialog(false)}
        folders={folders}
        selectedFolder={selectedFolder}
        onSelect={setSelectedFolder}
        rootLabel={t('folders.root')}
        rootValue="all"
        t={t}
      />

      {/* Edit Tags Dialog */}
      <Dialog open={!!editTagsDoc} onOpenChange={() => setEditTagsDoc(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag size={20} />
              {t('documents.editTags')}
            </DialogTitle>
          </DialogHeader>
          
          {editTagsDoc && (
            <div className="space-y-4">
              <p className="text-sm text-[#52525B]">{editTagsDoc.original_filename}</p>
              
              <div>
                <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                  {t('documents.tags')}
                </Label>
                <Input
                  value={editTagsValue}
                  onChange={(e) => setEditTagsValue(e.target.value)}
                  placeholder={t('documents.tagsPlaceholder')}
                  className="input-swiss mt-1"
                  data-testid="edit-tags-input"
                />
                <p className="text-xs text-[#A1A1AA] mt-1">{t('documents.tagsHint')}</p>
                
                {/* Suggested Tags */}
                {editSuggestedTags.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-[#52525B] mb-1">{t('documents.suggestedTags')}:</p>
                    <div className="flex flex-wrap gap-1">
                      {editSuggestedTags.map((tagObj) => (
                        <button
                          key={tagObj.tag}
                          onClick={() => addEditSuggestedTag(tagObj.tag)}
                          className="inline-flex items-center px-2 py-0.5 text-xs bg-[#F4F4F5] text-[#52525B] rounded-sm hover:bg-[#E4E4E7] transition-colors"
                          type="button"
                        >
                          + {tagObj.tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditTagsDoc(null)}
              className="rounded-sm"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSaveTags}
              disabled={savingTags}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
              data-testid="save-tags-btn"
            >
              {savingTags ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={closePreview}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={20} />
              {previewDoc?.original_filename}
            </DialogTitle>
          </DialogHeader>
          
          <div className="min-h-[400px] max-h-[60vh] overflow-auto bg-[#F4F4F5] rounded-sm flex items-center justify-center">
            {previewLoading ? (
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-sm text-[#52525B]">{t('common.loading')}</p>
              </div>
            ) : previewUrl && previewDoc ? (
              previewDoc.content_type?.includes('pdf') ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-[60vh] border-0"
                  title={previewDoc.original_filename}
                />
              ) : previewDoc.content_type?.includes('image') ? (
                <img
                  src={previewUrl}
                  alt={previewDoc.original_filename}
                  className="max-w-full max-h-[60vh] object-contain"
                />
              ) : previewDoc.content_type?.includes('text') ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-[60vh] border-0 bg-white"
                  title={previewDoc.original_filename}
                />
              ) : (
                <p className="text-sm text-[#52525B]">{t('documents.noPreview')}</p>
              )
            ) : null}
          </div>

          <DialogFooter>
            <Button
              onClick={() => previewDoc && handleDownload(previewDoc)}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
            >
              <DownloadSimple size={18} className="mr-2" />
              {t('documents.download')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF/A Preview Dialog */}
      <Dialog open={!!previewPdfaDoc} onOpenChange={closePreviewPdfa}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive size={20} className="text-green-600" />
              {previewPdfaDoc?.original_filename} - PDF/A
            </DialogTitle>
          </DialogHeader>
          
          <div className="min-h-[400px] max-h-[60vh] overflow-auto bg-[#F4F4F5] rounded-sm flex items-center justify-center">
            {previewPdfaUrl ? (
              <iframe
                src={previewPdfaUrl}
                className="w-full h-[60vh] border-0"
                title={`${previewPdfaDoc?.original_filename} - PDF/A`}
              />
            ) : (
              <p className="text-sm text-[#52525B]">{t('common.loading')}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              onClick={() => previewPdfaDoc && handleDownloadPdfa(previewPdfaDoc)}
              className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
            >
              <DownloadSimple size={18} className="mr-2" />
              {t('documents.downloadPdfa')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Document Details Dialog */}
      <Dialog open={!!viewDoc} onOpenChange={() => setViewDoc(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{viewDoc?.original_filename}</DialogTitle>
          </DialogHeader>
          {viewDoc && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                    {t('documents.size')}
                  </p>
                  <p className="text-sm text-[#09090B]">{formatBytes(viewDoc.size)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                    {t('documents.version')}
                  </p>
                  <p className="text-sm text-[#09090B]">v{viewDoc.version}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                    {t('documents.date')}
                  </p>
                  <p className="text-sm text-[#09090B]">
                    {formatDate(viewDoc.created_at, localeMap[language])}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                    Content Type
                  </p>
                  <p className="text-sm text-[#09090B]">{viewDoc.content_type}</p>
                </div>
              </div>
              
              {/* PDF/A Status */}
              <div className="p-3 bg-[#F4F4F5] rounded-sm">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-2">
                  {t('documents.archiveVersion')}
                </p>
                {viewDoc.has_pdfa ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={18} className="text-green-600" weight="fill" />
                      <span className="text-sm font-medium text-green-700">
                        {t('documents.pdfaAvailable')}
                      </span>
                      {viewDoc.pdfa_size && (
                        <span className="text-xs text-[#52525B]">
                          ({formatBytes(viewDoc.pdfa_size)})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handlePreviewPdfa(viewDoc)}
                        variant="outline"
                        size="sm"
                        className="rounded-sm text-green-700 border-green-600 hover:bg-green-50"
                      >
                        <Eye size={16} className="mr-1" />
                        {t('documents.preview')}
                      </Button>
                      <Button
                        onClick={() => handleDownloadPdfa(viewDoc)}
                        variant="outline"
                        size="sm"
                        className="rounded-sm text-green-700 border-green-600 hover:bg-green-50"
                      >
                        <Archive size={16} className="mr-1" />
                        {t('documents.downloadPdfa')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-[#A1A1AA]">
                    <X size={18} />
                    <span className="text-sm">{t('documents.pdfaNotAvailable')}</span>
                  </div>
                )}
              </div>
              
              {/* Tags */}
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-2">
                  {t('documents.tags')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {viewDoc.tags && viewDoc.tags.length > 0 ? (
                    viewDoc.tags.map((tag, idx) => (
                      <Badge 
                        key={idx} 
                        variant="secondary"
                        className="bg-[#E3F2FD] text-[#002FA7]"
                      >
                        {tag}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-[#A1A1AA]">—</span>
                  )}
                  <button
                    onClick={() => {
                      setViewDoc(null);
                      openEditTags(viewDoc);
                    }}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-[#002FA7] hover:bg-[#E3F2FD] rounded-sm transition-colors"
                  >
                    <PencilSimple size={12} />
                    {t('documents.editTags')}
                  </button>
                </div>
              </div>
              
              {/* Workflow Status */}
              {viewDoc.workflow_status && (
                <div className="p-3 bg-[#F4F4F5] rounded-sm">
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-2">
                    Stato Workflow
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {viewDoc.workflow_status === 'pending' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded">
                          <Clock size={12} />
                          {t('documents.workflowStatus.pending')}
                        </span>
                      )}
                      {viewDoc.workflow_status === 'changes_requested' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded">
                          <PencilSimple size={12} />
                          {t('documents.workflowStatus.changes_requested')}
                        </span>
                      )}
                      {(viewDoc.workflow_status === 'approved' || viewDoc.workflow_status === 'archived') && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                          <CheckCircle size={12} weight="fill" />
                          {t('documents.workflowStatus.approved')}
                        </span>
                      )}
                      {viewDoc.workflow_status === 'rejected' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
                          <XCircle size={12} />
                          {t('documents.workflowStatus.rejected')}
                        </span>
                      )}
                      {viewDoc.approved_by_email && (
                        <span className="text-xs text-[#52525B]">
                          da {viewDoc.approved_by_email}
                        </span>
                      )}
                    </div>
                    <Button
                      onClick={() => {
                        setViewDoc(null);
                        loadWorkflowHistory(viewDoc);
                      }}
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      disabled={loadingHistory}
                    >
                      <FlowArrow size={16} className="mr-1" />
                      Storico Workflow
                    </Button>
                  </div>
                </div>
              )}
              
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                  {t('documents.hash')}
                </p>
                <p className="hash-display text-xs break-all">{viewDoc.hash_sha256}</p>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                {isPreviewable(viewDoc.content_type, viewDoc.filename) && (
                  <Button
                    onClick={() => {
                      setViewDoc(null);
                      handlePreview(viewDoc);
                    }}
                    variant="outline"
                    className="rounded-sm"
                  >
                    <Eye size={18} className="mr-2" />
                    {t('documents.preview')}
                  </Button>
                )}
                <Button
                  onClick={() => handleDownload(viewDoc)}
                  className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
                >
                  <DownloadSimple size={18} className="mr-2" />
                  {t('documents.download')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDoc} onOpenChange={() => setDeleteDoc(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('documents.deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#E32636] hover:bg-[#C62828] text-white"
              data-testid="confirm-delete"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Submit for Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invia per Approvazione</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-[#F4F4F5] p-3 rounded">
              <p className="text-sm font-medium text-[#09090B]">{approvalDoc?.original_filename}</p>
              <p className="text-xs text-[#52525B]">
                {approvalDoc && formatBytes(approvalDoc.size)}
              </p>
            </div>
            
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                Approvatore *
              </Label>
              <Select value={selectedApprover} onValueChange={setSelectedApprover}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Seleziona approvatore" />
                </SelectTrigger>
                <SelectContent>
                  {approvers.map(approver => (
                    <SelectItem key={approver.id} value={approver.id}>
                      {approver.name} ({approver.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {approvers.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  Nessun approvatore disponibile. Contatta l'amministratore.
                </p>
              )}
            </div>
            
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                Note
              </Label>
              <Input
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Note opzionali per l'approvatore..."
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleSubmitForApproval}
              disabled={submittingApproval || !selectedApprover}
              className="bg-[#002FA7] hover:bg-[#002075] text-white"
            >
              {submittingApproval ? 'Invio...' : 'Invia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workflow History Dialog */}
      <Dialog open={showWorkflowHistory} onOpenChange={setShowWorkflowHistory}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlowArrow size={20} />
              Storico Workflow
            </DialogTitle>
          </DialogHeader>
          {workflowHistory && (
            <div className="space-y-4">
              <div className="bg-[#F4F4F5] p-3 rounded-sm">
                <p className="text-sm font-medium text-[#09090B]">{workflowHistory.document_name}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-[#52525B]">
                  <span>Richiesto da: <strong>{workflowHistory.requester_email}</strong></span>
                  <span>Approvatore: <strong>{workflowHistory.approver_email}</strong></span>
                </div>
              </div>
              
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {workflowHistory.history?.map((entry, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#002FA7]/10 flex items-center justify-center">
                      {(entry.action === 'submitted' || entry.action === 'resubmitted') && (
                        <CaretRight size={16} className="text-[#002FA7]" />
                      )}
                      {entry.action === 'approve' && (
                        <CheckCircle size={16} className="text-green-600" />
                      )}
                      {entry.action === 'reject' && (
                        <XCircle size={16} className="text-red-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-[#09090B]">
                          {entry.action === 'submitted' && 'Inviato per approvazione'}
                          {entry.action === 'resubmitted' && 'Reinviato per approvazione'}
                          {entry.action === 'approve' && 'Approvato'}
                          {entry.action === 'reject' && 'Rifiutato'}
                        </span>
                      </div>
                      <p className="text-xs text-[#52525B]">
                        {entry.user_email} • {formatDate(entry.timestamp, localeMap[language])}
                      </p>
                      {entry.notes && (
                        <p className="mt-1 text-sm text-[#52525B] bg-white p-2 rounded border border-[#E4E4E7]">
                          {entry.notes}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWorkflowHistory(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Detection Dialog */}
      <AlertDialog open={!!duplicateFile} onOpenChange={() => {
        setDuplicateFile(null);
        setDuplicateExisting(null);
        setPendingUploads([]);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Warning size={24} className="text-amber-500" />
              {t('documents.duplicateDetected')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>{t('documents.duplicateMessage')}</p>
              {duplicateFile && duplicateExisting && (
                <div className="bg-[#F4F4F5] p-3 rounded-sm space-y-2">
                  <div className="flex items-center gap-2">
                    <FileText size={20} className="text-[#52525B]" />
                    <span className="font-medium">{duplicateFile.name}</span>
                  </div>
                  <div className="text-sm text-[#52525B] grid grid-cols-2 gap-2">
                    <div>
                      <span className="font-medium">{t('documents.currentVersion')}:</span>
                      <p>v{duplicateExisting.version} • {formatBytes(duplicateExisting.size)}</p>
                      <p className="text-xs">{formatDate(duplicateExisting.created_at, localeMap[language])}</p>
                    </div>
                    <div>
                      <span className="font-medium">Nuovo file:</span>
                      <p>{formatBytes(duplicateFile.size)}</p>
                    </div>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel onClick={() => handleDuplicateDecision('skip')}>
              {t('documents.skipFile')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDuplicateDecision('update')}
              className="bg-[#002FA7] hover:bg-[#002075]"
              data-testid="update-version-btn"
            >
              <ClockCounterClockwise size={16} className="mr-2" />
              {t('documents.updateVersion')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Version History Dialog */}
      <Dialog open={showVersionHistory} onOpenChange={setShowVersionHistory}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClockCounterClockwise size={24} className="text-[#002FA7]" />
              {t('documents.versionHistory')}
            </DialogTitle>
          </DialogHeader>
          
          {versionHistoryDoc && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-[#F4F4F5] rounded-sm">
                <FileText size={24} className="text-[#002FA7]" />
                <div>
                  <p className="font-medium text-sm">{versionHistoryDoc.original_filename}</p>
                  <p className="text-xs text-[#52525B]">
                    {t('documents.currentVersion')}: v{versionHistoryDoc.version}
                  </p>
                </div>
              </div>
              
              {loadingVersions ? (
                <div className="py-8 text-center text-[#52525B]">
                  {t('common.loading')}
                </div>
              ) : documentVersions.length === 0 ? (
                <div className="py-8 text-center text-[#52525B]">
                  {t('documents.noVersions')}
                </div>
              ) : (
                <div className="max-h-[300px] overflow-y-auto space-y-2">
                  {documentVersions.map((version, index) => (
                    <div
                      key={version.id}
                      className={`flex items-center justify-between p-3 border rounded-sm ${
                        index === 0 ? 'border-[#002FA7] bg-blue-50' : 'border-[#E4E4E7]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          index === 0 ? 'bg-[#002FA7] text-white' : 'bg-[#E4E4E7] text-[#52525B]'
                        }`}>
                          v{version.version}
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {index === 0 && <Badge className="mr-2 bg-[#002FA7]">{t('documents.currentVersion')}</Badge>}
                            {formatBytes(version.size)}
                          </p>
                          <p className="text-xs text-[#52525B]">
                            {version.created_by_email} • {formatDate(version.created_at, localeMap[language])}
                          </p>
                          {version.has_pdfa && (
                            <Badge variant="outline" className="mt-1 text-xs text-green-600 border-green-600">
                              PDF/A
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadVersion(version.id)}
                        className="rounded-sm"
                        data-testid={`download-version-${version.version}`}
                      >
                        <DownloadSimple size={16} className="mr-1" />
                        {t('documents.download')}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVersionHistory(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
